import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import TenantDetails from "../../components/admin/TenantDetails";
import LoadingState from "../../components/admin/LoadingState";
import ErrorState from "../../components/admin/ErrorState";
import ConfirmModal from "../../components/admin/ConfirmModal";
import useAuth from "../../hooks/useAuth";
import { updateTenantStatus, deleteTenant } from "../../api/tenantApi";
import { getBot } from "../../api/chatbotApi";
import { fetchTenantServices, createTenantService, updateTenantService } from "../../api/tenantServiceApi";
import { fetchTenantConfigs, updateTenantConfigs } from "../../api/tenantConfigApi";
import { activateTenant, fetchOnboardingStatus, startOnboarding, updateOnboardingStep } from "../../api/onboardingApi";
import { fetchTenantAuditLogs } from "../../api/auditApi";
import { canWriteInTenant, getAccessDeniedMessage, isPlatformAdmin } from "../../util/accessControl";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Toast from "../../components/admin/Toast";

const extractList = (response) => response?.data?.items || response?.data || response || [];
const extractObject = (response) => response?.data || response || null;

const VALID_TABS = ["overview", "services", "config", "onboarding", "audit"];

const TAB_FETCH_MAP = {
  services: "services",
  config: "config",
  onboarding: "onboarding",
  audit: "audit",
};

const AccessDeniedPage = ({ message, tenantId }) => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="max-w-lg rounded-3xl border border-rose-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h1 className="mt-4 text-xl font-semibold text-slate-950">Access denied</h1>
      <p className="mt-2 text-sm text-slate-600">{message || getAccessDeniedMessage()}</p>
      <div className="mt-6 flex justify-center gap-3">
        <Link to="/agents" className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]">
          <ArrowLeft size={16} />
          Back
        </Link>
        <Link to="/dashboard" className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          Dashboard
        </Link>
      </div>
    </div>
  </div>
);

const TenantDetailPage = () => {
  const { tenant_id: tenantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isMetaConfig = location.pathname.endsWith("/meta-config");

  const initialTab = searchParams.get("tab");
  const safeInitialTab = VALID_TABS.includes(initialTab) && !(isMetaConfig && initialTab === "config") ? initialTab : "overview";

  const isDisabled = !canWriteInTenant(user, tenantId);
  // bypass condition for testing
  // const isDisabled = false;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingSteps, setOnboardingSteps] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState(safeInitialTab);

  const loadedTabs = useRef(new Set());
  const tabLoading = useRef({});
  const [tabLoadingState, setTabLoadingState] = useState({});
  const [tabErrors, setTabErrors] = useState({});

  useEffect(() => {
    const tab = searchParams.get("tab");
    const safeTab = VALID_TABS.includes(tab) && !(isMetaConfig && tab === "config") ? tab : "overview";
    setActiveTab(safeTab);
    if (isMetaConfig && tab === "config") {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "overview");
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, isMetaConfig, setSearchParams]);

  useEffect(() => {
    if (location.state?.toast) {
      setToast(location.state.toast);
      setTimeout(() => setToast(null), 3000);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const [statusValue, setStatusValue] = useState("draft");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchTabData = useCallback(async (tab) => {
    if (loadedTabs.current.has(tab)) return;
    if (tabLoading.current[tab]) return;

    tabLoading.current[tab] = true;
    setTabLoadingState((prev) => ({ ...prev, [tab]: true }));
    setTabErrors((prev) => ({ ...prev, [tab]: "" }));

    try {
      if (tab === "overview") {
        try {
          const res = await fetchOnboardingStatus(tenantId);
          const record = extractObject(res);
          setOnboarding(record);
          setOnboardingSteps(record?.steps || record?.tenant_onboarding_steps || []);
        } catch (_) {}
      } else if (tab === "services") {
        const res = await fetchTenantServices(tenantId);
        setServices(extractList(res));
      } else if (tab === "config") {
        const res = await fetchTenantConfigs(tenantId);
        setConfigs(extractList(res));
      } else if (tab === "onboarding") {
        const res = await fetchOnboardingStatus(tenantId);
        const record = extractObject(res);
        setOnboarding(record);
        setOnboardingSteps(record?.steps || record?.tenant_onboarding_steps || []);

        if (!loadedTabs.current.has("services")) {
          try {
            const svcRes = await fetchTenantServices(tenantId);
            setServices(extractList(svcRes));
            loadedTabs.current.add("services");
          } catch (_) {}
        }
        if (!loadedTabs.current.has("config")) {
          try {
            const cfgRes = await fetchTenantConfigs(tenantId);
            setConfigs(extractList(cfgRes));
            loadedTabs.current.add("config");
          } catch (_) {}
        }
      } else if (tab === "audit") {
        const res = await fetchTenantAuditLogs(tenantId);
        setAuditLogs(extractList(res));
      }
      loadedTabs.current.add(tab);
    } catch (err) {
      setTabErrors((prev) => ({ ...prev, [tab]: err.message || "Failed to load data." }));
    } finally {
      tabLoading.current[tab] = false;
      setTabLoadingState((prev) => ({ ...prev, [tab]: false }));
    }
  }, [tenantId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
    fetchTabData(tab);
  };

  useEffect(() => {
    const loadTenant = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getBot(tenantId);
        const record = extractObject(res);
        setTenant(record);
        setStatusValue(record?.status || "draft");
        fetchTabData(safeInitialTab);
        if (safeInitialTab !== "overview") {
          try {
            const onboardingRes = await fetchOnboardingStatus(tenantId);
            const onboardingRecord = extractObject(onboardingRes);
            setOnboarding(onboardingRecord);
            setOnboardingSteps(onboardingRecord?.steps || onboardingRecord?.tenant_onboarding_steps || []);
          } catch (_) {}
        }
      } catch (err) {
        setError(err.message || "Failed to load tenant.");
      } finally {
        setLoading(false);
      }
    };
    loadTenant();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const withActionState = async (task) => {
    setActionError("");
    setActionLoading(true);
    try {
      await task();
    } catch (taskError) {
      setActionError(taskError.message || "Action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const refreshCurrentTab = useCallback(async () => {
    loadedTabs.current.delete(activeTab);
    loadedTabs.current.delete("overview");
    await fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  const handleToggleService = async (service) => {
    const newStatus = service.status === "enabled" ? "disabled" : "enabled";
    const SERVICE_NAMES = {
      customer_support: "Chat Support",
      document_rag: "Smart Document Search",
      voice_support: "Voice Support",
      // appointment_booking: "Appointment Booking",
    };
    const name = SERVICE_NAMES[service.service_key] || service.service_key;
    await withActionState(async () => {
      const exists = services.some((s) => s.service_key === service.service_key);
      if (exists) {
        await updateTenantService(tenantId, service.service_key, {
          status: newStatus,
          config_json: service.config_json || {},
        });
      } else {
        await createTenantService(tenantId, {
          service_key: service.service_key,
          status: newStatus,
          config_json: service.config_json || {},
        });
      }
      loadedTabs.current.delete("services");
      await refreshCurrentTab();
      setToast(`${name} ${newStatus === "enabled" ? "enabled" : "disabled"} successfully`);
      setTimeout(() => setToast(null), 3000);
    });
  };

  const handleStatusChangeRequest = (newStatus) => {
    if (newStatus === tenant?.status) return;
    setStatusValue(newStatus);
    setStatusConfirmOpen(true);
  };
  const handleConfirmStatusChange = () => withActionState(async () => {
    await updateTenantStatus(tenantId, { status: statusValue });
    const res = await getBot(tenantId);
    setTenant(extractObject(res));
    setStatusConfirmOpen(false);
  });
  const handleCancelStatusChange = () => {
    setStatusValue(tenant?.status || "draft");
    setStatusConfirmOpen(false);
  };
  const handleDeleteTenant = () => setDeleteOpen(true);
  const handleConfirmDelete = () => withActionState(async () => {
    await deleteTenant(tenantId);
    navigate("/agents");
  });
  const handleStartOnboarding = () => withActionState(async () => {
    await startOnboarding(tenantId);
    try {
      const res = await fetchOnboardingStatus(tenantId);
      const record = extractObject(res);
      setOnboarding(record);
      setOnboardingSteps(record?.steps || record?.tenant_onboarding_steps || []);
    } catch (_) {}
    await refreshCurrentTab();
  });
  const handleStepUpdate = (stepKey, dataJson = {}) => withActionState(async () => {
    await updateOnboardingStep(tenantId, stepKey, {
      completed: true,
      data_json: dataJson,
    });
    await refreshCurrentTab();
  });
  const handleActivateTenant = () => withActionState(async () => {
    await activateTenant(tenantId);
    const res = await getBot(tenantId);
    setTenant(extractObject(res));
  });

  const handleConfigSave = async (configKey, configJson) => {
    await withActionState(async () => {
      await updateTenantConfigs(tenantId, { config_key: configKey, config_json: configJson });
      loadedTabs.current.delete("config");
      await fetchTabData("config");
      setToast("Config saved successfully");
      setTimeout(() => setToast(null), 3000);
    });
  };

  const retryLoad = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getBot(tenantId);
      setTenant(extractObject(res));
      setStatusValue(extractObject(res)?.status || "draft");
      loadedTabs.current.add("overview");
    } catch (err) {
      setError(err.message || "Failed to load business.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState label="Loading business details..." />;
  if (error) {
    const is403 = error.includes("403") || error.toLowerCase().includes("forbidden") || error.toLowerCase().includes("access denied");
    if (is403) return <AccessDeniedPage message={error} tenantId={tenantId} />;
    return <ErrorState description={error} onRetry={retryLoad} />;
  }
  if (!tenant) return <ErrorState description="Business not found." onRetry={retryLoad} />;

  const backPath = location.state?.from || "/agents";
  const backLabel = location.state?.fromLabel || "Bots";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link
          to={backPath}
          className="flex items-center gap-1 font-medium transition hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Back to {backLabel}
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">{tenant.name}</span>
      </div>

      {isPlatformAdmin(user) && !isMetaConfig ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#096dea]">Business management</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-950 lg:text-3xl">Business details</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Manage business settings, users, services, onboarding, and activity.
          </p>
        </div>
      ) : null}

      <Toast message={toast} visible={!!toast} onDismiss={() => setToast(null)} />

      {actionError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionError}
        </div>
      ) : null}

      <TenantDetails
        tenant={tenant}
        tenantId={tenantId}
        hideOwnerAndIsolation={location.pathname.endsWith("/meta-config")}
        hideAiServices={location.pathname.endsWith("/meta-config")}
        hideOperatingHoursAndAiBehavior={location.pathname.endsWith("/meta-config")}
        hideBusinessProfile={location.pathname.endsWith("/meta-config")}
        hideMetadata={isMetaConfig}
        hideConfigTab={isMetaConfig}
        hidePublicProfileLink={isMetaConfig && activeTab === "overview"}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        services={services}
        configs={configs}
        onboarding={onboarding}
        onboardingSteps={onboardingSteps}
        auditLogs={auditLogs}
        onServiceToggle={handleToggleService}
        onStartOnboarding={handleStartOnboarding}
        onStepUpdate={handleStepUpdate}
        onActivateTenant={handleActivateTenant}
        onDeleteTenant={handleDeleteTenant}
        statusValue={statusValue}
        onStatusValueChange={handleStatusChangeRequest}
        isDisabled={isDisabled}
        userRole={user?.role}
        onConfigSave={handleConfigSave}
      />

      <ConfirmModal
        open={statusConfirmOpen}
        title="Change status"
        description={`Are you sure you want to change the status to "${statusValue}"?`}
        confirmLabel={actionLoading ? "Saving..." : "Confirm"}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
        loading={actionLoading}
      />
      <ConfirmModal
        open={deleteOpen}
        title="Delete business"
        description="Are you sure you want to delete this business? This action cannot be undone."
        confirmLabel={actionLoading ? "Deleting..." : "Delete"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteOpen(false)}
        loading={actionLoading}
      />
    </div>
  );
};

export default TenantDetailPage;
